import React, {useEffect, useRef, useState} from 'react'
import {BaseButton, Flex, Text, useDisclosure} from '../../../../vanilla'
import {useRapidDeliveryStore, useStoreDetailsStore, useBasketStore} from '../../../../store/hooks/useStore'
import {observer} from 'mobx-react'
import {RapidDeliveryConfirmationModal} from '../rapid-delivery-confirmation-modal'
import {Countdown} from '../../../../components/book-delivery/reservation-status'
import {CheckIcon} from '../../../../components/icons'
import {isMobile} from '../../../../utils/utils'
import {useAddShippingDetailsTracking, useViewShippingDetailsTracking} from '../../../../analytics/shipping-tracking'
import {useSlotExpiration} from '../../../../store/hooks/useSlotExpiration'
import {ExclamationCircleIcon} from '../../../../components/icons'
import {RapidDeliveryInfo} from './rapid-delivery-info'
import { BookingSlotErrorModal } from '../booking-slot-error-modal'
import { useSlotReservation } from '../../../../store/hooks/useSlotReservation'
import { BookRapidDeliveryResponse } from '../../../../types/store/rapidDelivery'

interface RapidDeliveryProps {
  onRequestDeliveryTypeChange?: (onConfirm: () => void) => void
}

export const RapidDelivery = observer(({onRequestDeliveryTypeChange}: RapidDeliveryProps) => {
  const {
    isRapidDeliveryBooked,
    rapidDeliveryDeliveryEstimate,
    bookRapidDelivery,
    rapidDeliveryShippingCost,
    rapidDeliveryReservation,
    isBasketOverRapidDeliveryItemLimit,
  } = useRapidDeliveryStore()
  const { currentDeliveryStore } = useStoreDetailsStore()
  const {currentDeliverySlotId} = useBasketStore()
  const sendViewShippingDetailsData = useViewShippingDetailsTracking()
  const sendAddShippingDetailsData = useAddShippingDetailsTracking()
  const hasTrackedRapidViewRef = useRef(false)
  const {isReservationExpired} = useSlotExpiration()
  const [isReservationExpiredLocal, setIsReservationExpiredLocal] = useState(false)
  const isRapidReservationExpired = isReservationExpiredLocal || isReservationExpired()
  const activeRapidDeliverySlot = isRapidDeliveryBooked && !isRapidReservationExpired
  const [bookSlotErrorCode, setBookSlotErrorCode] = useState('success')
  const {handleShippingAddressValidation} = useSlotReservation()

  const confirmationModal = useDisclosure(false)
  const errorBookingSlotModal = useDisclosure(false)

  const proceedWithRapidBooking = async () => {
    try {

      if (!handleShippingAddressValidation()) {
        setBookSlotErrorCode('300')
        errorBookingSlotModal.onOpen()
        return
      }

      const result = await bookRapidDelivery() as unknown as BookRapidDeliveryResponse
      setIsReservationExpiredLocal(false)
      const rapidSlotId = 'rapid-delivery'

      if (result.code !== 'success') {
        setBookSlotErrorCode(result.code)
        errorBookingSlotModal.onOpen()
        return
      }

      sendAddShippingDetailsData({
        deliveryDay: {
          isRapid: true,
        },
        slot: {
          id: rapidSlotId,
          available: true,
          storeId: currentDeliveryStore?.id ?? '',
          shippingCost: rapidDeliveryShippingCost ?? rapidDeliveryReservation?.shippingCost ?? 0,
          windowStartTime: rapidDeliveryReservation?.windowStartTime ?? '',
          startHour: '',
          endHour: '',
        },
        currentDeliverySlotId: rapidSlotId,
      })
      confirmationModal.onOpen()
    } catch (error) {
      setBookSlotErrorCode('300')
      errorBookingSlotModal.onOpen()
      console.error('Failed to book rapid delivery:', error)
    }
  }

  const handleBookRapidDelivery = () => {
    const hasActiveSlotToChange = currentDeliverySlotId && !isRapidReservationExpired

    if (hasActiveSlotToChange && onRequestDeliveryTypeChange) {
      onRequestDeliveryTypeChange(proceedWithRapidBooking)
      return
    }
    proceedWithRapidBooking()
  }

  const deliveryEstimate = rapidDeliveryDeliveryEstimate ?? 60
  const expiryTime = rapidDeliveryReservation?.expiryTime

  // Fire view_shipping_details event
  useEffect(() => {
    if (hasTrackedRapidViewRef.current) {
      return
    }

    hasTrackedRapidViewRef.current = true

    const rapidSlotId = 'rapid-delivery'

    sendViewShippingDetailsData({
      deliveryDay: {
        isRapid: true,
      },
      slots: [
        {
          id: rapidSlotId,
          available: true,
          storeId: currentDeliveryStore?.id ?? '',
          shippingCost: rapidDeliveryShippingCost ?? rapidDeliveryShippingCost ?? 0,
          windowStartTime: rapidDeliveryReservation?.windowStartTime ?? new Date().toISOString(),
          startHour: '',
          endHour: '',
        },
      ],
      currentDeliverySlotId: isRapidDeliveryBooked ? rapidSlotId : '',
      slotsMatch: (slotId: string, selectedSlotId: string) => slotId === selectedSlotId,
    })
  }, [
    currentDeliveryStore?.id,
    isRapidDeliveryBooked,
    rapidDeliveryShippingCost,
    rapidDeliveryReservation?.windowStartTime
  ])

  const getButtonText = () => {
    if (isRapidReservationExpired) {
      return 'Rebook'
    }
    let text = 'Book Rapid Now'
    if (isRapidDeliveryBooked) {
      text = 'Booked'
    }
    if (isMobile() && !isRapidDeliveryBooked) {
      text = 'Book Now'
    }
    return text
  }

  return (
    <>
      <Flex
        flexDirection="column"
        backgroundColor="white"
        width="full"
        paddingX={["16px", "24px"]}
        paddingTop="18px"
        paddingBottom="24px"
        alignItems="flex-start"
        data-test-selector="rapid-delivery-container"
      >
                <Flex
          alignItems="center"
          justifyContent="space-between"
          width="full"
          paddingBottom="18px"
        >
          <Text variant="heading1">
            Rapid Delivery
          </Text>
        </Flex>
        <Flex
          flexDirection="column"
          gap="4px"
          marginBottom="16px"
          width="full" borderTop="1px"
          borderColor="gray200"
          paddingTop="24px"
        >
          <Text variant="text6" data-test-selector="rapid-delivery-estimate">
            Get your shop delivered in under {deliveryEstimate} minutes.
          </Text>
          {rapidDeliveryShippingCost && (
            <Flex gap="4px">
                            <Text variant="text3">
                Delivery fee: 
              </Text>
              <Text variant="text5" data-test-selector="rapid-delivery-shipping-cost">
                £{rapidDeliveryShippingCost.toFixed(2)}
              </Text>
            </Flex>
          )}
        </Flex>
        <Flex
          gap={["16px", "24px"]}
          alignItems={["flex-start", "center"]}
          width={["full", "auto"]}
          flexDirection={["column", "row"]}
          justifyContent={["flex-start", "space-between"]}
          >
          <BaseButton
            size="sm"
            variant="primary"
            minHeight={["48px", "40px"]}
            width={["150px", "200px"]}
            onClick={!isRapidDeliveryBooked || isRapidReservationExpired ? handleBookRapidDelivery : undefined}
            disabled={!isRapidDeliveryBooked && isBasketOverRapidDeliveryItemLimit}
            data-test-selector="rapid-delivery-book-button"
          >
            {activeRapidDeliverySlot && <CheckIcon marginRight="2px" />}
            <Text variant="unstyled" fontSize="inherit" lineHeight="inherit" paddingX="10px">
              {getButtonText()}
            </Text>
          </BaseButton>
          {isRapidReservationExpired && (
            <Flex gap="4px" alignItems="center" marginLeft={["0px", "12px"]}>
              <ExclamationCircleIcon color="accent0" boxSize="16px" flexShrink="0" />
              <Text variant="text3" color="accent0" data-test-selector="rapid-delivery-expired-message">
                Your Rapid Delivery reservation has expired!
              </Text>
            </Flex>
          )}
          </Flex>
        {/* Countdown or Info */}
        {activeRapidDeliverySlot ? (
          <Flex
            width={["full", "half"]}
            backgroundColor="gray50"
            paddingY="12px"
            paddingX="32px"
            style={{borderRadius: "4px"}}
            data-test-selector="rapid-delivery-countdown"
            gap="4px"
            alignItems="center"
            justifyContent="center"
            flexWrap="wrap"
            marginTop="24px"
          >
            <Text variant="text3">Checkout in the next:</Text>
            {expiryTime ? (
              <Countdown
                slotExpiration={expiryTime}
                color="accent0"
                textVariant="text2"
                onCountdownEnd={() => setIsReservationExpiredLocal(true)}
              />
            ) : (
              <Text variant="text2">--:--</Text>
            )}
            <Text variant="text3">to secure your delivery</Text>
          </Flex>
        ) : (
          <RapidDeliveryInfo />
        )}
      </Flex>

      <RapidDeliveryConfirmationModal
        {...confirmationModal}
      />

      <BookingSlotErrorModal
        {...errorBookingSlotModal}
        statusCode={bookSlotErrorCode}
        isValidShippingAddress={handleShippingAddressValidation()}
        />
    </>
  )
})
